import * as React from 'react';
import { useDispatch } from 'react-redux';
import { PagedApiResult } from '../../models/utilities/paged-request';
import { ShipmentMethod } from '../../transportation/models/enums/shipment-method';
import { OrderSearchFilters } from '../../transportation/models/filters/order-search-filters';
import { OrderSummary } from '../../transportation/models/shared/order-summary';
import { LoadingInlinePanel } from '../loading/LoadingInline';
import { PageHeading } from '../page-components/PageHeading';
import { PagedList } from '../paged-list/PagedList';
import FlyoutOrderControlRow from './FlyoutOrderControlRow';

let IGNORE_UNMOUNT_SELECTABLE = false;

interface IProps {
    isFetching: boolean;
    availableOrders: OrderSummary[];
    selectedOrders: OrderSummary[];
    onMoveDone: any;
    entityName: string;
}

export const FlyoutOrderControl = (props : IProps) => {

    const dispatch                          = useDispatch();
    const [filterParams]                    = React.useState(new OrderSearchFilters(ShipmentMethod.PickUp));
    const [selectedTab, setSelectedTab]     = React.useState(0);
    const [toAdd, setToAdd]                 = React.useState<OrderSummary[]>([]);
    const [toRemove, setToRemove]           = React.useState<OrderSummary[]>([]);
    
    const [ordersOnPickup, setOrdersOnPickup] = React.useState<PagedApiResult<OrderSummary>>(() => {
        const initial = new PagedApiResult<OrderSummary>();
        initial.pagination = filterParams.paginationInfo;        
        return initial;
    });

    const [availableOrders, setAvailableOrders] = React.useState<PagedApiResult<OrderSummary>>(() => {
        const initial = new PagedApiResult<OrderSummary>();
        initial.pagination = filterParams.paginationInfo;        
        return initial;
    });
    
    React.useEffect(() => {
        if (props.selectedOrders) {
            const onPickup = new PagedApiResult<OrderSummary>();
            onPickup.pagination = filterParams.paginationInfo;
            onPickup.results = [...props.selectedOrders];
            setOrdersOnPickup(onPickup);
        }
    }, [props.selectedOrders]);

    React.useEffect(() => {
        if (props.availableOrders) {
            const availables = new PagedApiResult<OrderSummary>();
            availables.pagination = filterParams.paginationInfo;
            availables.results = [...props.availableOrders];
            setAvailableOrders(availables);
        }
    }, [props.availableOrders]);    

    /**
     * Handler for when the user is done drag selecting items
     * @param evt The draggable event with selected items
     */   
    const onDragComplete = (evt : any) => {
        
        // The select library we use calls dragComplete
        // whenever a component is unmounted.  However,
        // that causes issues.  Hacky, but it's what's 
        // required without changing implemenation.
        if (IGNORE_UNMOUNT_SELECTABLE) {
            return;
        }

        let o;
        if (selectedTab === 0) {
            o = [...ordersOnPickup.results];
        }
        else {
            o = [...availableOrders.results];
        }

        for (let i = 0; i < o.length; i++) {

            let foundEvtMatch = false;
            const orderItem = o[i];
            
            for (let j = 0; j < evt.length; j++) {
                const evtItem = evt[j];
                if (evtItem.props.orderItem.id === orderItem.id) {
                    foundEvtMatch = true;
                    break;
                }
            }

            o[i].isChecked = (foundEvtMatch) ? true : false;
        }

        // Update the running totals
        const selecteds = o.filter((itm : OrderSummary) => {
            return itm.isChecked;
        });

        if (selectedTab === 0) {
            setToRemove(selecteds);
        }
        else {
            setToAdd(selecteds);
        }

    };

    /**
     * Called after selecting available orders.  Moves the toAdd list
     * to the editing stop and removes the toAdd list from the available
     * order list
     */
    const moveOrders = () => {

        IGNORE_UNMOUNT_SELECTABLE = true;

        if (toAdd || toRemove) {

            let tmpChangeList;
            let tmpListA;
            let tmpListB;

            if (selectedTab === 0) {
                tmpChangeList = toRemove;
                tmpListA = {...availableOrders};
                tmpListA.results = tmpListA.results.concat(toRemove);
                tmpListB = {...ordersOnPickup};
            }
            else {
                tmpChangeList = toAdd;
                tmpListA = {...ordersOnPickup};
                tmpListA.results = tmpListA.results.concat(toAdd);
                tmpListB = {...availableOrders};
            }

            for (let j = 0; j < tmpChangeList.length; j++) {
                for (let i = tmpListB.results.length - 1; i >= 0; i--) {
                    if ( tmpListB.results[i].id ===  tmpChangeList[j].id) {
                        tmpListB.results.splice(i, 1);
                        break;
                    }
                }
            }

            if (selectedTab === 0) {
                setAvailableOrders(tmpListA);
                setOrdersOnPickup(tmpListB);
                props.onMoveDone(tmpListB.results);
            }
            else {                
                setOrdersOnPickup(tmpListA);
                setAvailableOrders(tmpListB);                
                props.onMoveDone(tmpListA.results);
            }

            setToAdd([]);
            setToRemove([]);

            setTimeout(() => {
                IGNORE_UNMOUNT_SELECTABLE = false;
            }, 200);

        }
    };

    /**
     * Get the correct title to show for the orders on the truck tab
     */
    const getOrdersTitle = () => {
        if (ordersOnPickup && ordersOnPickup.results.length > 0) {
            return `Orders (${ ordersOnPickup.results.length })`;
        }
        else {
            return 'Orders';
        }
    };

    /**
     * Get the correct title to show for the orders available tab
     */
    const getAvailableOrdersTitle = () => {

        if (props.isFetching) {
            return <LoadingInlinePanel message="Fetching Orders" />;
        }
        if (availableOrders && availableOrders.results && availableOrders.results.length > 0) {
            return `Available to Add (${ availableOrders.results.length })`;
        }
        else {
            return 'Available to Add (0)';
        }        
    };

    /**
     * Clear selections and flip tabs
     * @param e The React event
     * @param val A 1 or 0 indicating which tab is active
     */
    const onTabChange = (e: any, val: any) => {
        clearSelectedOrders();
        setSelectedTab(val);
    };

    /**
     * Clear all the selected orderes for both lists
     */
    const clearSelectedOrders = () => {

        IGNORE_UNMOUNT_SELECTABLE = true;

        const tmpAvail = {...availableOrders};
        tmpAvail.results.forEach((itm : OrderSummary) => {
            itm.isChecked = false;
        });

        setToAdd([]);
        setAvailableOrders(tmpAvail);

        const tmpOn = {...ordersOnPickup};
        tmpOn.results.forEach((itm : OrderSummary) => {
            itm.isChecked = false;
        });

        setToRemove([]);
        setOrdersOnPickup(tmpOn);
 
        setTimeout(() => {
            IGNORE_UNMOUNT_SELECTABLE = false;
        }, 200);
    };
    
    return <React.Fragment>
                <div className="row flyout-order-control">
                    <div className="col-md-12">
                        
                        <PageHeading
                            title=""
                            useTabs={true}                                    
                            tabLabels={[ getOrdersTitle(), getAvailableOrdersTitle() ]}
                            onTabChange={ onTabChange }
                            selectedTab={ selectedTab } />
                            
                                {
                                    selectedTab === 0 
                                        ?  <PagedList
                                            noResultsMessage="There are no orders selected yet."
                                            searchCallback={ () => { return; } }
                                            data={ ordersOnPickup }
                                            filterProps={ filterParams }
                                            selectableClass="transport-order-selectable"
                                            onFilterUpdate={ () => { return; } }
                                            skipScroll={ true }
                                            hidePager={ true }
                                            reduxDispatch={ dispatch }
                                            enabledDrag={ true }
                                            useClientSelectablePositioning={ true }
                                            onDragComplete={ onDragComplete }
                                            rowRenderer={(order: OrderSummary, extraProps : any) => 
                                                <FlyoutOrderControlRow key={ `stop-order-${order.id}`} orderItem={ order } {...extraProps} />
                                            }
                                        />
                                        : null
                                }
                                {
                                    selectedTab === 1
                                        ?  <PagedList
                                                noResultsMessage="No available orders found!"
                                                searchCallback={ () => { return; } }
                                                data={ availableOrders }
                                                filterProps={ filterParams }
                                                onFilterUpdate={ () => { return; } }
                                                skipScroll={ true }
                                                hidePager={ true }
                                                selectableClass="transport-order-selectable"
                                                reduxDispatch={ dispatch }
                                                enabledDrag={ true }
                                                useClientSelectablePositioning={ true }
                                                onDragComplete={ onDragComplete }
                                                rowRenderer={(order: OrderSummary, extraProps : any) => 
                                                    <FlyoutOrderControlRow key={ `stop-order-${order.id}`} orderItem={ order } {...extraProps}  />
                                                }
                                            />  
                                        : null
                                }
                            </div>
                        </div>

                {
                    toAdd.length > 0 || toRemove.length > 0
                        ? <div className="stopaction-add-move-orders">
                            {
                                selectedTab === 1
                                    ? <button 
                                        disabled={ toAdd.length < 1 || props.isFetching }
                                        onClick={ moveOrders }>
                                            Add to { props.entityName }
                                    </button>
                                    : null
                            }
                            {
                                selectedTab === 0                                
                                    ? <button 
                                        className="negative"
                                        disabled={ toRemove.length < 1 || props.isFetching }
                                        onClick={ moveOrders }>
                                            Remove from { props.entityName }
                                    </button>
                                    : null
                            }
                        </div>
                        : null
                }
    </React.Fragment>;

};
